const ANDROID = 'android';
const IOS = 'ios';

/**
 * Prüft, ob die native Firebase-Bridge der App verfügbar ist.
 *
 * Android muss `addJavascriptInterface` vor `loadUrl` aufrufen, iOS registriert
 * den Handler an der `WKWebView`-Configuration — beide sind also gesetzt, bevor
 * unser Script läuft. Damit ist die Prüfung gleichzeitig die Unterscheidung
 * zwischen App-Webview und Webshop.
 *
 * @returns {boolean}
 */
export function isBridgeAvailable() {
    return detectBridge() !== null;
}

/**
 * Übergibt ein Event an das Firebase-SDK der App.
 *
 * @param {string} name Event-Name, bereits Firebase-konform
 * @param {Object} params Event-Parameter, bereits Firebase-konform
 */
export function logEvent(name, params) {
    if (!name) {
        return;
    }

    switch (detectBridge()) {
        case ANDROID:
            callAndroid('logEvent', name, JSON.stringify(params));
            break;
        case IOS:
            postToIos({
                command: 'logEvent',
                name: name,
                parameters: params
            });
            break;
        default:
            console.warn('[FirebaseBridge] No native APIs found, cannot log event', name);
    }
}

/**
 * Setzt eine User Property im Firebase-SDK der App.
 *
 * @param {string} name
 * @param {string} value
 */
export function setUserProperty(name, value) {
    if (!name) {
        return;
    }

    switch (detectBridge()) {
        case ANDROID:
            callAndroid('setUserProperty', name, value);
            break;
        case IOS:
            postToIos({
                command: 'setUserProperty',
                name: name,
                value: value
            });
            break;
        default:
            console.warn('[FirebaseBridge] No native APIs found, cannot set user property', name);
    }
}

function detectBridge() {
    if (typeof window === 'undefined') {
        return null;
    }
    if (window.AnalyticsWebInterface) {
        return ANDROID;
    }
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.firebase) {
        return IOS;
    }
    return null;
}

/**
 * Ruft eine Methode der Android-Bridge auf, sofern die App sie exponiert.
 *
 * Methoden ohne `@JavascriptInterface`-Annotation sind in JS `undefined`. Ein
 * ungeprüfter Aufruf würde werfen und damit auch den restlichen Versand des
 * Events abbrechen — eine unvollständige Bridge soll aber nur die betroffene
 * Methode kosten, nicht das Event.
 *
 * Auf iOS ist die Prüfung nicht möglich: `postMessage` existiert immer, welche
 * Kommandos der native Handler kennt, ist von JS aus nicht erkennbar.
 * @private
 */
function callAndroid(method, ...args) {
    const bridge = window.AnalyticsWebInterface;
    if (typeof bridge[method] !== 'function') {
        console.warn('[FirebaseBridge] Android bridge does not implement', method);
        return;
    }
    bridge[method](...args);
}

function postToIos(message) {
    window.webkit.messageHandlers.firebase.postMessage(message);
}
